import type { PropertyFilterProps } from "@amzn/awsui-components-react/polaris";

/**
 * The guest-owner display suffix.
 *
 * NOT a global source of truth: two CONSUMERS hardcode the suffix's SHAPE and
 * do NOT import this constant, so changing its value silently breaks BOTH —
 * update them by hand:
 *   1. usePncFilteredOwnerOptions: `option.label?.includes("(Guest)")`.
 *   2. cleanOwnerValue in server-side-table/propertyFilterTransform.ts:
 *      `value.replace(/ \(Guest\)$/, "")` — the regex-escaped parens hide it
 *      from a plain-text grep.
 *
 * slice.ts imports this constant, so this module must not import from the
 * slice — that would be a cycle.
 */
export const GUEST_SUFFIX = " (Guest)";

/**
 * @internal Filter property KEYS whose values are owner aliases.
 *
 * These are property KEYS, not column ids — Audits' "Action pending" column id
 * is "actionOwner" but its filter key is "statusActionOwner". Confusing the two
 * silently drops the suffix.
 *
 * Typed `readonly string[]`, not `as const`: `as const` narrows to a literal
 * tuple and breaks `.includes(someString)` under strict TS.
 */
export const OWNER_GUEST_LABEL_KEYS: readonly string[] = [
  "owner",
  "statusActionOwner",
];

/** The element type of a property's `operators` array. */
type Operator = NonNullable<
  PropertyFilterProps.FilteringProperty["operators"]
>[number];

/**
 * Owner label for a single alias, used by every cell / option / inline label
 * site. Returns `""` for `""`, so keep each call site's `|| "…"` fallback
 * OUTSIDE the call to reproduce today's output byte-for-byte.
 */
export const formatGuestLabel = (value: string, guests: Set<string>): string =>
  guests.has(value) ? `${value}${GUEST_SUFFIX}` : value;

/**
 * @internal Applied-token text for the property filter. Re-implements
 * Cloudscape's built-in enum join (`", "`) and suffixes each element
 * independently; enum tokens carry a `string[]`, value tokens a `string`.
 */
export const formatGuestTokenValue = (
  value: string | readonly string[],
  guests: Set<string>,
): string =>
  Array.isArray(value)
    ? value.map((v) => formatGuestLabel(String(v), guests)).join(", ")
    : formatGuestLabel(String(value), guests);

/**
 * @internal Attaches `format` to the `=` and `!=` operators only, promoting
 * plain operator strings to `{ operator, format }` objects (preserving
 * `tokenType` / `match` / `form` on entries promoted from objects). Accepts
 * both extended operator objects and plain strings.
 *
 * An empty guest set returns `baseOperators` as-is, so callers need no guard.
 *
 * WARNING — the one wiring that matters. `format` MUST be
 *   `(v) => formatGuestTokenValue(v, guests)`
 * and NOT `(v) => formatGuestLabel(String(v), guests)`. A custom `format`
 * SUPPRESSES Cloudscape's built-in enum join (installed only when no custom
 * format exists) and is called ONCE with the WHOLE `token.value` — a
 * `string[]` for an enum token. The `formatGuestLabel` variant would render a
 * multi-value Owner token as `"a,b"` instead of `"a, b"` and drop per-element
 * suffixing — a regression this operator promotion itself introduces.
 */
export const createGuestLabeledOperators = (
  baseOperators: readonly Operator[] | undefined,
  guests: Set<string>,
):
  | readonly (Operator | { operator: string; format: (v: any) => string })[]
  | undefined => {
  if (guests.size === 0) {
    return baseOperators;
  }
  return baseOperators?.map((op) => {
    const operator = typeof op === "string" ? op : op.operator;
    if (operator !== "=" && operator !== "!=") {
      return op;
    }
    const base = typeof op === "string" ? { operator } : op;
    return { ...base, format: (v: any) => formatGuestTokenValue(v, guests) };
  });
};

/**
 * Replaces `operators` on owner-ish properties (those whose `key` is in
 * `OWNER_GUEST_LABEL_KEYS`) with the guest-labeled operator set.
 *
 * WARNING — this is a DISPLAY copy. NEVER pass the result to `useCollection`,
 * which matches items with its OWN copy of `filteringProperties`; the entire
 * filter-half safety argument rests on the decorated copy being separate from
 * the matching copy. Decorate `propertyFilterProps` AFTER `useCollection`
 * returns, never the array handed into it.
 */
export const decorateGuestFilteringProperties = (
  properties: readonly PropertyFilterProps.FilteringProperty[],
  guests: Set<string>,
): readonly PropertyFilterProps.FilteringProperty[] => {
  if (guests.size === 0) {
    return properties;
  }
  return properties.map((property) =>
    OWNER_GUEST_LABEL_KEYS.includes(property.key)
      ? {
          ...property,
          operators: createGuestLabeledOperators(property.operators, guests),
        }
      : property,
  );
};

/**
 * Attaches `label` to an option only when its `propertyKey` is owner-ish and
 * its `value` is a guest alias. Every other option — including non-guest owner
 * options — keeps no `label`.
 */
export const decorateGuestFilteringOptions = (
  options: readonly PropertyFilterProps.FilteringOption[],
  guests: Set<string>,
): readonly PropertyFilterProps.FilteringOption[] => {
  if (guests.size === 0) {
    return options;
  }
  return options.map((option) =>
    OWNER_GUEST_LABEL_KEYS.includes(option.propertyKey) &&
    guests.has(option.value)
      ? { ...option, label: formatGuestLabel(option.value, guests) }
      : option,
  );
};

/**
 * True when `attributeName` names an owner-alias field, i.e. one whose value
 * should carry the guest suffix. Normalizes BOTH shapes the activity log
 * emits before matching `OWNER_GUEST_LABEL_KEYS` (which is camelCase-only): a
 * `custom_` prefix on re-emitted custom-attribute changes, and snake_case on
 * raw DDB names — activity-log `changeLogs` genuinely carry both forms of the
 * same field (e.g. `statusActionOwner` and `status_action_owner`). Folding
 * them here keeps every form of an owner key decorating identically. The
 * `custom_` strip is deliberately unanchored, matching `isDateField` and
 * `getUIFieldName` in the activity-log-split-panel utils.
 *
 * The values these guard are HISTORICAL — read from activity-log `changeLogs`,
 * i.e. who the owner WAS at each past update — while the guest roster reflects
 * grants as of NOW. Suffixing is deliberate: the label answers "is this person
 * a guest?", not "was this person a guest then?". The consequence is that a
 * past owner since promoted to a member renders bare, while a past member
 * since made a guest renders suffixed — which keeps the same alias consistent
 * with every adjacent surface rather than following per-column labeling rules.
 */
export const isOwnerGuestLabelKey = (attributeName: string): boolean => {
  const clean = attributeName.replace("custom_", "");
  const camel = clean.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  return OWNER_GUEST_LABEL_KEYS.includes(camel);
};
